import React, { useState } from "react";
import { FiCode } from "react-icons/fi";

import { useOrganizationMembership } from "../../hooks/useOrganizationMembership";
import { buildPostSampleForModel } from "../../services/postSampleService";
import { downloadJson } from "../../utils/modelsList";

interface PostSampleButtonProps {
  modelId: string;
  modelName?: string | null;
  /** "icon" for card and table rows, "labelled" for a modal footer */
  variant?: "icon" | "labelled";
  /** Where a failure goes; without it the button shows the message beside itself */
  onError?: (message: string) => void;
  className?: string;
}

// Admins only: downloads the body this model's run POSTs to its template's executionUrl, inputs included
// (src/utils/processedModelPostSample.ts), so endpoint developers can copy the real shape into erode-models.
const PostSampleButton: React.FC<PostSampleButtonProps> = ({ modelId, modelName, variant = "icon", onError, className = "" }) => {
  const { isFullAdmin } = useOrganizationMembership();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isFullAdmin) return null;

  const download = async (e: React.MouseEvent) => {
    // The button sits on clickable cards: building the sample must not also open the model
    e.stopPropagation();
    setBusy(true);
    setError(null);
    try {
      // A partial sample (an input that couldn't be read) says so in its own _sampleNotes key
      const { fileName, body } = await buildPostSampleForModel(modelId);
      downloadJson(JSON.stringify(body, null, 2), fileName);
    } catch (err: any) {
      const message = err?.message || "Could not build the sample POST body.";
      console.error("[PostSample] failed:", err);
      if (onError) onError(message);
      else setError(message);
    } finally {
      setBusy(false);
    }
  };

  const title = "Download sample executionUrl POST body (Admin)";
  return (
    <>
      <button
        type="button"
        onClick={download}
        disabled={busy}
        title={title}
        aria-label={`Download sample POST body for ${modelName || "this model"}`}
        data-testid="post-sample"
        className={`${variant === "icon" ? "eai-btn-icon" : "eai-btn-outline"} disabled:pointer-events-none disabled:opacity-40 ${className}`}
      >
        <FiCode size={variant === "icon" ? 12 : 14} />
        {variant === "labelled" && (busy ? "Building…" : "Sample POST")}
      </button>
      {error && (
        <span className="font-inter text-eai-body-xs text-eai-danger-text" role="alert" data-testid="post-sample-error">
          {error}
        </span>
      )}
    </>
  );
};

export default PostSampleButton;
